/* =============================================================
   COUNTRIES
   =============================================================
   The reference list behind every country field in the panel,
   loaded once per session and shared.

   It is fetched rather than hardcoded because `public.countries`
   is also what the foreign keys on `berths` and `companies`
   validate against. A hardcoded copy here could offer a country
   the database would then reject, or miss one somebody added —
   reading the same rows makes that impossible by construction.

   Public data: the select policy covers `anon`, so this resolves
   before anyone signs in.
   ============================================================= */

import { computed, ref } from 'vue'
import { supabase } from './supabase'
import { toDataError } from './errors'
import type { Country } from './types'

const countries = ref<Country[]>([])
const failed = ref(false)

let ready: Promise<void> | null = null

async function fetchCountries(): Promise<void> {
  // PostgrestBuilder is a thenable, not a Promise — it has no .catch — so the
  // error handling lives in an async function rather than in a chain.
  try {
    const { data, error } = await supabase
      .from('countries')
      .select('code, name')
      .order('name', { ascending: true })

    if (error) throw toDataError(error, 'Could not load the country list.')
    countries.value = data ?? []
    failed.value = false
  } catch {
    // Swallowed on purpose: a missing country list must not stop the app
    // booting. CountrySelect reports it where it actually matters.
    failed.value = true
  }
}

/** Sorted by name, which is the order a dropdown wants. Idempotent. */
export function initCountries(): Promise<void> {
  ready ??= fetchCountries()
  return ready
}

/** code → name, for tables and detail pages. */
const byCode = computed(() => new Map(countries.value.map((c) => [c.code, c.name])))

export function useCountries() {
  return {
    countries,
    failed,
    /**
     * The country's name, or the raw code if the list has not loaded.
     * Showing "DE" beats showing nothing or an empty cell.
     */
    countryName: (code: string): string => byCode.value.get(code) ?? code,
  }
}
